import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";

import { cn } from "../lib/utils";

/**
 * Card primitive. Growth Fusion theme — solid white panels, warm hairline
 * borders, soft warm shadow. Glass surfaces are reserved for the Sheet
 * primitive; legacy "glass-*" variants here resolve to solid surfaces so
 * existing call sites still compile cleanly.
 */
export const cardVariants = cva("rounded-[var(--radius-lg)] border", {
  variants: {
    surface: {
      solid: "bg-[var(--color-surface)] border-[var(--color-border-hairline)] shadow-[var(--shadow-card)]",
      soft:  "bg-[var(--color-surface-soft)] border-transparent",
      /* Legacy aliases — all collapse to solid per the new spec. */
      glass: "bg-[var(--color-surface)] border-[var(--color-border-hairline)] shadow-[var(--shadow-card)]",
      "glass-strong": "bg-[var(--color-surface)] border-[var(--color-border-hairline)] shadow-[var(--shadow-card)]",
      "glass-deep": "bg-[var(--color-surface)] border-[var(--color-border-hairline)] shadow-[var(--shadow-card)]",
    },
    padding: {
      none: "",
      sm: "p-3",
      md: "p-5",
      lg: "p-8",
    },
  },
  defaultVariants: { surface: "solid", padding: "md" },
});

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, surface, padding, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ surface, padding, className }))}
      {...props}
    />
  ),
);
Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col gap-1.5 pb-3", className)}
      {...props}
    />
  ),
);
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-[17px] font-semibold tracking-tight", className)}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn("text-sm text-[var(--color-text-soft)]", className)}
      {...props}
    />
  ),
);
CardDescription.displayName = "CardDescription";

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-center gap-2 pt-4", className)}
      {...props}
    />
  ),
);
CardFooter.displayName = "CardFooter";
